import generateId from 'src/helpers/generateId';
import {BaseError} from 'src/internal/common/errors';
import logErrorInDev from 'src/internal/common/logErrorInDev';
import {getFutureEvent} from 'src/internal/errors/sentry/beforeSend';
import {
  extractErrorMessage,
  normalizeErrorLevel,
} from 'src/internal/errors/sentry/utils';
import getAelErrorsTransport from 'src/internal/errors/transport/getAelErrorsTransport';

import type {
  Event,
  EventHint,
  EventProcessor,
  Integration,
} from '@sentry/types';
import type {SentryEvent} from 'src/internal/errors/sentry/types';
import type {AelErrorEvent} from 'src/internal/errors/transport/types';
import type {ObservabilityConfig} from 'src/types';

const MAX_ERROR_MESSAGE_LENGTH = 150;
const SENTRY_EVENT_URL =
  'https://sentry.corp.stripe.com/organizations/stripe/issues/?project=-1&query=';

type AddGlobalEventProcessor = (callback: EventProcessor) => void;

type IntegrationConfig = Pick<
  ObservabilityConfig,
  'service' | 'fetch' | 'flushSignal'
>;

export default class SentryAelIntegration implements Integration {
  name: string;
  config: IntegrationConfig;

  constructor(
    config: IntegrationConfig,
    integrationName = 'SentryAelIntegration',
  ) {
    // The name should be unique to attribute the errors to the correct service
    this.name = `${integrationName}__${config.service}`;
    this.config = config;
  }

  setupOnce(addGlobalEventProcessor?: AddGlobalEventProcessor): void {
    if (!addGlobalEventProcessor) {
      // addGlobalEventProcessor is removed in Sentry v8
      // If this integration is used with Sentry v8+ processEvent hook will be called
      return;
    }
    addGlobalEventProcessor((event, hint) => {
      if (!event.sdk?.integrations?.includes(this.name)) {
        return event;
      }

      return this.processEvent(event, hint);
    });
  }

  processEvent<E extends SentryEvent>(event: E, hint?: EventHint): E {
    try {
      this.sendAelRequest(event, hint);
    } catch (e) {
      logErrorInDev(new BaseError(`Failed to send error to AEL: ${e}`));
    }
    return event;
  }

  sendAelRequest(event: SentryEvent, hint?: EventHint): void {
    // Don't send AEL request if the error is not reported to Sentry
    if (!this.config.service || !getFutureEvent(event, hint)) {
      return;
    }

    const eventId = event.event_id;
    const level = normalizeErrorLevel(event.level);
    const errorMessage = getErrorMessage(event) ?? '<unknown>';

    const params: AelErrorEvent['params'] = {
      ...Object.fromEntries(
        Object.entries(event.tags ?? {}).filter(
          ([_key, value]) => value !== null && value !== undefined,
        ),
      ),
      release: event.release ?? '<unknown>',
      user_id: event.user?.id ?? '<unknown>',
      error_level: level,
      error_issue_id: eventId ?? '<unknown>',
      error_issue_url: buildSentryEventUrl(eventId),
      domain_name: getDomainName(),
    };

    getAelErrorsTransport(this.config.service, {
      fetch: this.config.fetch,
      flushSignal: this.config.flushSignal,
    }).send({
      event_id: generateId(),
      error_message: errorMessage,
      created: `${Math.floor(new Date().getTime() / 1000)}`,
      params,
    });
  }
}

function getErrorMessage(event: Pick<Event, 'exception'>): string {
  const errorMessage = extractErrorMessage(event) ?? '<unknown>';

  return errorMessage
    .substring(0, MAX_ERROR_MESSAGE_LENGTH)
    .replace(
      /([^\w]+(cus|acct|org|usr|sub|pi|prod|in|qry|fee|file|qfl)_)[A-Za-z0-9]{10,}/g,
      '$1[REDACTED]',
    );
}

function getDomainName(): string {
  if (typeof window === 'undefined') {
    return '<unknown>';
  }

  return window.location?.hostname ?? '<unknown>';
}

function buildSentryEventUrl(eventId: string | undefined): string {
  return eventId ? `${SENTRY_EVENT_URL}${eventId}` : '<unknown>';
}
